import pako from 'pako';

/**
 * URPExporter - Handles exporting modified .urp files
 * 
 * Architecture:
 * - Takes XML string and compresses it using gzip
 * - Triggers browser download of the compressed .urp file
 * - Maintains original file structure and format
 */
export class URPExporter {
  /**
   * Export XML as a .urp file (gzipped)
   * @param {string} xmlString - The XML content to export
   * @param {string} fileName - Desired output filename (without extension)
   */
  static exportToURPFile(xmlString, fileName = 'program') {
    try {
      // Ensure filename doesn't have .urp extension already
      const baseName = fileName.replace(/\.urp$/, '');

      // Convert string to Uint8Array
      const textEncoder = new TextEncoder();
      const xmlBytes = textEncoder.encode(xmlString);

      // Compress using gzip
      const compressed = pako.gzip(xmlBytes);

      // Create blob and download
      const blob = new Blob([compressed], { type: 'application/gzip' });
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}.urp`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error exporting URP file:', error);
      throw new Error(`Failed to export .urp file: ${error.message}`);
    }
  }

  /**
   * Export XML as a readable .xml file (for debugging/inspection)
   * @param {string} xmlString - The XML content to export
   * @param {string} fileName - Desired output filename (without extension)
   */
  static exportToXMLFile(xmlString, fileName = 'program') {
    try {
      const baseName = fileName.replace(/\.(xml|urp)$/, '');

      const blob = new Blob([xmlString], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}.xml`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error exporting XML file:', error);
      throw new Error(`Failed to export .xml file: ${error.message}`);
    }
  }

  /**
   * Validate XML before export
   * @param {string} xmlString - XML string to validate
   * @returns {boolean} True if valid
   */
  static validateXML(xmlString) {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      const parserError = xmlDoc.querySelector('parsererror');
      
      if (parserError) {
        throw new Error('Invalid XML: ' + parserError.textContent);
      }

      // Check for URProgram root element
      if (xmlDoc.documentElement.tagName !== 'URProgram') {
        throw new Error('Invalid URP file: missing URProgram root element');
      }

      return true;
    } catch (error) {
      console.error('XML validation failed:', error);
      return false;
    }
  }
}
